/**
 * CausalityModule — Unified Single Canvas Implementation
 * All strategic methods (Impact Mapping, CLD, Value Tree, North Star) on ONE canvas
 */

import Config from '../../core/Config.js';
import * as ActionRegistry from '../../core/ActionRegistry.js';
import { createLayoutPanel, createPanelActions, PANEL_VARIANTS } from '../../utils/panel.js';
import { installSplit } from '../../utils/Splitter.js';
import container from '../../core/ServiceContainer.js';
import { SERVICE_TOKENS as T } from '../../core/services.tokens.js';
import Logger from '../../utils/Logger.js';

const log = Logger.scope('CausalityModule');

export class CausalityModule {
  constructor() {
    this.container = null;
    this.unmountCanvas = null;
    this._mounted = false;
    this._selectedEdge = null;
    this._selectedNode = null;
    this._crossLinkProposals = [];
    this._activeLenses = new Set(); // Active lens filters
    this._overlayPanels = new Map(); // Active overlay panels
  }

  async initialize() {
    // Register actions for unified canvas
    ActionRegistry.registerMap('causality', {
      'causality:set-start-node': async ({ dataset }) => {
        const nodeId = dataset?.nodeId || '';
        try { localStorage.setItem('causality.startNodeId', nodeId); } catch(_) {}
        // Optionally apply filter anchor immediately
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.applyFilters({ linkedTo: nodeId || null, linkedMode: 'network' }); } catch(_) {}
      },
      // Explicit lens selection
      'causality:set-lens': async ({ dataset }) => { await this._setLens(dataset?.lensId || 'okr'); },
      // Quick edge creation from canvas
      'causality:quick-create-edge': async (event) => {
        const { from, to } = event.detail || {};
        if (!from || !to) return;
        
        const svc = container.get(T.CAUSALITY);
        if (!svc) return;
        
        try {
          const created = await svc.createEdge({ 
            fromExternalId: from, 
            toExternalId: to,
            type: 'influences',
            polarity: '+',
            strength: 0.5,
            confidence: 0.5,
            evidenceLevel: 'hypothesis',
            notes: 'Created via canvas interaction'
          });
          // Incremental update: notify canvas to append only the new edge
          try {
            const { default: Bus } = await import('../../services/graph/CanvasBus.js');
            Bus.emit('causality:edge-added', { edge: created });
          } catch(_) {}
          log.info('Edge created via canvas', { from, to });
        } catch (e) {
          log.error('Failed to create edge', e);
        }
      },
      
      // Core canvas actions
      'causality:create-edge': async (ctx) => {
        if (Config?.FEATURES?.CAUSALITY_READ_ONLY === true) return; // read-only guard
        const root = ctx?.target?.closest?.('.causality-module') || document;
        const from = root.querySelector('#edge-from')?.value?.trim?.();
        const to = root.querySelector('#edge-to')?.value?.trim?.();
        if (!from || !to) return;
        const svc = container.get(T.CAUSALITY);
        if (!svc) return;
        await svc.createEdge({ fromExternalId: from, toExternalId: to });
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.refresh(); } catch(_) {}
      },
      
      'causality:delete-edge': async ({ dataset }) => {
        if (Config?.FEATURES?.CAUSALITY_READ_ONLY === true) return; // read-only guard
        const id = dataset?.id ? parseInt(dataset.id, 10) : undefined;
        if (!id) return;
        const svc = container.get(T.CAUSALITY);
        await svc.deleteEdge(id);
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.refresh(); } catch(_) {}
      },
      
      'causality:save-edge': async ({ dataset }) => {
        if (Config?.FEATURES?.CAUSALITY_READ_ONLY === true) return; // read-only guard
        const id = dataset?.id ? parseInt(dataset.id, 10) : (this._selectedEdge?.id ? parseInt(this._selectedEdge.id, 10) : null);
        if (!id) return;
        const root = this.container;
        const type = root.querySelector('#edge-edit-type')?.value || 'influences';
        const pol = root.querySelector('#edge-edit-polarity')?.value || '+';
        const strength = parseFloat(root.querySelector('#edge-edit-strength')?.value || '0.5');
        const confidence = parseFloat(root.querySelector('#edge-edit-confidence')?.value || '0.5');
        const evidenceLevel = root.querySelector('#edge-edit-evidence')?.value || 'anecdotal';
        const lastValidated = root.querySelector('#edge-edit-lastValidated')?.value || '';
        const notes = root.querySelector('#edge-edit-notes')?.value || '';
        const svc = container.get(T.CAUSALITY);
        await svc.updateEdge(id, { type, polarity: pol, strength, confidence, evidenceLevel, lastValidated, notes });
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.refresh(); } catch(_) {}
      },
      
      // Lens actions (filters/highlights on same canvas)
      'causality:toggle-lens': ({ dataset }) => {
        const lens = dataset?.lens;
        if (!lens) return;
        this.toggleLens(lens);
      },
      
      // Overlay panel actions
      'causality:open-impact-map': () => { /* no-op: drawer removed */ },
      
      'causality:open-lever-explorer': () => {
        this.openOverlay('lever-explorer');
      },
      
      'causality:open-evidence-ledger': () => { /* no-op: drawer removed */ },
      
      'causality:open-loop-narrator': ({ dataset }) => {
        const loopId = dataset?.loopId;
        this.openOverlay('loop-narrator', { loopId });
      },
      
      'causality:close-overlay': ({ dataset }) => {
        const overlayId = dataset?.overlayId;
        this.closeOverlay(overlayId);
      },
      
      // Cross-link and anti-silo actions
      'causality:apply-cross-link': async ({ dataset }) => {
        const from = dataset?.from;
        const to = dataset?.to;
        const type = dataset?.type || 'influences';
        if (!from || !to) return;
        
        const svc = container.get(T.CAUSALITY);
        await svc.createEdge({
          fromExternalId: from,
          toExternalId: to,
          type: type,
          polarity: '+',
          strength: 0.6,
          confidence: 0.5,
          notes: 'Created via anti-silo cross-link proposal'
        });
        
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.refresh(); } catch(_) {}
        log.info('Applied cross-link proposal', { from, to });
      },
      
      // Canvas control actions
      'causality:refresh': async () => { 
        try { 
          const { default: Bus } = await import('../../services/graph/CanvasBus.js');
          const f = this._filters || { owners: [], onlyLinked: false, ownerLinkedMode: 'none' };
          Bus.applyFilters({ owners: f.owners||[], onlyLinked: !!f.onlyLinked, ownerLinkedMode: f.ownerLinkedMode || 'none' });
          Bus.refresh();
        } catch(_) {}
      },
      
      'causality:layout': async () => {
        try { 
          document.dispatchEvent(new Event('causality:apply-layout')); 
        } catch (_) {}
      },
      
      'causality:focus-node': ({ dataset }) => {
        const nodeId = dataset?.nodeId;
        if (nodeId) {
          this.focusNode(nodeId);
        }
      },
      
      'causality:suggest-links': async () => {
        const svc = container.get(T.CAUSALITY);
        await svc.suggestLinksLocal(this._selectedNode);
        await this.renderSuggestions();
      }
    });
  }

  activate() {}

  async mount(host) {
    this.container = host;
    this._mounted = true;
    
    // Listen for quick edge creation from canvas
    this._quickCreateHandler = async (event) => {
      console.log('CausalityModule: Received edge creation event', event.detail);
      const { from, to } = event.detail || {};
      if (!from || !to) {
        console.error('Missing from or to in edge creation', { from, to });
        return;
      }
      
      const svc = container.get(T.CAUSALITY);
      if (!svc) {
        console.error('CausalGraphService not available');
        return;
      }
      
      try {
        console.log('Creating edge in service...', { from, to });
        const created = await svc.createEdge({ 
          fromExternalId: from, 
          toExternalId: to,
          type: 'influences',
          polarity: '+',
          strength: 0.5,
          confidence: 0.5,
          evidenceLevel: 'hypothesis',
          notes: 'Created via canvas interaction'
        });
        console.log('Edge created successfully', created);
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.emit('causality:edge-added', { edge: created }); } catch(_) {}
        log.info('Edge created via canvas', { from, to });
      } catch (e) {
        console.error('Failed to create edge:', e);
        log.error('Failed to create edge', e);
      }
    };
    
    document.addEventListener('causality:quick-create-edge', this._quickCreateHandler);
    // Listen for node-add-related requests from canvas node tools
    this._quickAddRelatedHandler = async (event) => {
      try {
        const { anchorId, anchorKind, addKind } = event.detail || {};
        if (!anchorId || !addKind) return;
        const svc = container.get(T.CAUSALITY);
        const lens = (localStorage.getItem('causality.lens') || 'impact');
        let created = null;
        if (addKind === 'stakeholder') {
          const name = prompt('Stakeholder / Actor name'); if (!name) return;
          created = await svc.createStakeholder({ name });
          if (anchorKind === 'objective') await svc.createEdgeTyped(String(anchorId), String(created.id), 'influences', { confidence: 0.4 });
        } else if (addKind === 'behavior') {
          // Impact == Key Result (unified). Always create KR and link Objective -> KR
          const title = prompt('Key Result'); if (!title) return;
          created = await svc.createKeyResult({ title });
          // Determine objective to link: if anchor is objective, use it; if stakeholder, try to resolve parent objective via influences
          let objectiveId = null;
          if (anchorKind === 'objective') objectiveId = String(anchorId);
          else if (anchorKind === 'stakeholder' && this._graph) {
            const sid = String(anchorId);
            const e = (this._graph.edges||[]).find(e => String(e.type)==='influences' && String(e.toExternalId||e.toId||e.to)===sid && String(e.fromExternalId||e.fromId||e.from).startsWith('obj_'));
            if (e) objectiveId = String(e.fromExternalId||e.fromId||e.from);
          }
          // Fallback to focused objective
          if (!objectiveId && this._focusedNodeId && this._graph) {
            const node = (this._graph.nodes||[]).find(n => String(n.id)===String(this._focusedNodeId));
            if (node && node.kind==='objective') objectiveId = String(node.id);
          }
          if (objectiveId) await svc.createEdgeTyped(objectiveId, String(created.id), 'drives', { confidence: 0.6 });
        } else if (addKind === 'deliverable') {
          const title = prompt('Deliverable title'); if (!title) return;
          created = await svc.createDeliverable({ title });
          if (anchorKind === 'behavior') await svc.createEdgeTyped(String(anchorId), String(created.id), 'requires', { confidence: 0.5 });
        }
        try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.refresh(); } catch(_) {}
      } catch (e) { console.error('Quick add related failed', e); }
    };
    document.addEventListener('causality:node-add-related', this._quickAddRelatedHandler);
    // Listen for lane titles published by canvas (authoritative for Impact lens)
    try {
      const { default: Bus } = await import('../../services/graph/CanvasBus.js');
      this._offLaneTitles = Bus.on('causality:lane-titles', (ev) => {
        try {
          const list = ev?.detail?.titles || [];
          const map = {};
          list.forEach(t => { if (t && t.id) map[String(t.id)] = String(t.label || ''); });
          this._laneTitlesFromCanvas = map;
          // Ensure Focus picker reflects latest titles immediately
          try { this.renderStartNodePanel(this._graph); } catch(_) {}
        } catch(_) {}
      });
    } catch(_) {}
    
    await this.render();
  }

  async _setLens(raw) {
    const lensId = String(raw).toLowerCase().includes('impact') ? 'impact' : (String(raw).toLowerCase().includes('okr') ? 'okr' : raw);
    try { localStorage.setItem('causality.lens', lensId); } catch(_) {}
    // Notify both DOM listeners and CanvasBus consumers
    try { document.dispatchEvent(new CustomEvent('causality:set-lens', { detail: { lensId } })); } catch(_) {}
    try { const { default: Bus } = await import('../../services/graph/CanvasBus.js'); Bus.emit('causality:set-lens', { lensId }); } catch(_) {}
    // Re-render entire module to keep UI in sync without manual refresh
    try { await this.render(); } catch(_) {}
  }

  async render() {
    if (!this.container) return;
    const enabled = Config?.FEATURES?.CAUSALITY === true;
    let currentLens = 'impact';
    try { currentLens = localStorage.getItem('causality.lens') || 'impact'; } catch(_) {}

    // Controls toolbar (right panel): only lens picker + refresh (actions bar below)
    const controlsToolbar = `
      <div class="canvas-toolbar">
        <div class="lens-picker">
          <span class="label">Lens:</span>
          <button class="lens-pick ${currentLens==='okr'?'active':''}" data-action="causality:set-lens" data-lens-id="okr">OKR</button>
          <button class="lens-pick ${currentLens==='impact'?'active':''}" data-action="causality:set-lens" data-lens-id="impact">Impact Mapping</button>
        </div>
      </div>
    `;

    // Left panel: canvas + summary bar
    const canvasContent = `
      <!-- Summary bar -->
      <div id="causality-summary" class="summary-bar"></div>
      <!-- Main canvas container (always visible) -->
      <div class="canvas-container">
        <div id="causality-canvas-host" class="cld-canvas"></div>
        <!-- Overlay panels (positioned absolutely over canvas) -->
        <div id="overlay-container" class="overlay-container"></div>
      </div>
      ${!enabled ? '<p class="text-muted">Enable VITE_FEATURE_CAUSALITY to load the CLD canvas.</p>' : ''}
    `;

    // Right panel content: controls and selections
    const controlsContent = `
      ${controlsToolbar}
      <div class="controls-stack" style="display:flex;flex-direction:column;gap:12px;padding:8px 0;">
        <div id="start-node-panel"></div>
        <div id="filters-panel"></div>
        <div id="canvas-controls-panel"></div>
        <div id="loops-panel"></div>
        <div id="smells-panel"></div>
        <div id="suggestions-panel"></div>
        <div id="details-panel"></div>
        <div id="cross-links-panel"></div>
      </div>
    `;

    const actionsBar = (() => {
      const list = [ { label: '↻ Refresh', action: 'causality:refresh', variant: 'ghost', size: 'small' } ];
      return `<div class="panel-actions-inline">${createPanelActions(list)}</div>`;
    })();

    // Build two panels using split layout (reuse existing split CSS class)
    const leftPanel = createLayoutPanel({
      title: 'Business Levers - Canvas',
      variant: PANEL_VARIANTS.DEFAULT,
      className: 'causality-module unified-canvas',
      content: canvasContent,
      noPadding: false
    });

    const rightPanel = createLayoutPanel({
      title: 'Controls & Details',
      variant: PANEL_VARIANTS.DEFAULT,
      className: 'causality-controls-panel',
      content: `
        ${actionsBar}
        ${controlsContent}
      `
    });

    this.container.innerHTML = `
      <div class="causality-split-layout" data-split-container>
        ${leftPanel}
        ${rightPanel}
      </div>
    `;
    try {
      const style = document.createElement('style');
      style.textContent = `
        .canvas-toolbar{ display:flex; justify-content:space-between; align-items:center; gap:12px; }
        .lens-picker{ display:flex; align-items:center; gap:6px; }
        .lens-picker .label{ color:var(--color-text-secondary); font-size:12px; margin-right:2px; }
        .lens-pick{ border:1px solid var(--color-border); background:var(--color-bg); color:var(--color-text); border-radius:999px; padding:4px 10px; font-size:12px; }
        .lens-pick.active{ background:var(--color-primary); color:#fff; border-color:var(--color-primary); }
      `;
      this.container.querySelector('.panel')?.prepend(style);
    } catch(_) {}

    // Install resizable split (persist widths under 'causality')
    try { installSplit('.causality-split-layout', 'causality'); } catch (_) {}

    // Mount the canvas if enabled
    if (enabled) {
      await this.mountCanvas();
      // Apply persisted filters once to avoid two-phase flicker
      try {
        const { default: Bus } = await import('../../services/graph/CanvasBus.js');
        let persisted = null;
        try { persisted = JSON.parse(localStorage.getItem('causality.filters') || 'null'); } catch(_) { persisted = null; }
        const initFilters = persisted && typeof persisted === 'object' ? persisted : (this._filters || { owners: [], onlyLinked: false, ownerLinkedMode: 'none' });
        this._filters = initFilters;
        Bus.applyFilters({ owners: initFilters.owners||[], onlyLinked: !!initFilters.onlyLinked, ownerLinkedMode: initFilters.ownerLinkedMode||'none' });
      } catch(_) {}
    }

    // Initial data load (populate right panels and canvas)
    try { await this.refreshCanvas(); } catch (e) { log.error('Failed to refresh canvas', e); }

    // Bind controls fallback (ensures clicks work even if global router is disabled)
    try { this._bindControls(); } catch(_) {}
  }

  _bindControls() {
    const host = this.container || document;
    const buttons = host.querySelectorAll('.lens-pick');
    buttons.forEach(btn => {
      btn.removeEventListener?.('click', btn.__lensHandler);
      const handler = async (e) => {
        e.preventDefault(); e.stopPropagation();
        const which = btn.getAttribute('data-lens-id') || '';
        await this._setLens(which);
      };
      btn.addEventListener('click', handler);
      btn.__lensHandler = handler;
    });
  }

  async mountCanvas() {
    const canvasHost = this.container.querySelector('#causality-canvas-host');
    if (!canvasHost) return;
    
    try {
      const mod = await import('../../components/causality/CausalityCanvas.jsx');
      // Pull initial transform for current lens to avoid visual jump
      let initialTransform = null;
      try {
        const lens = localStorage.getItem('causality.lens') || 'impact';
        const k = `causality.transform.${lens}`;
        const stored = localStorage.getItem(k);
        if (stored) initialTransform = JSON.parse(stored);
      } catch(_) {}
      // Prepare initial filters from storage
      let initialFilters = { owners: [], onlyLinked: false, ownerLinkedMode: 'none' };
      try { const persisted = JSON.parse(localStorage.getItem('causality.filters')||'null'); if (persisted) initialFilters = persisted; } catch(_) {}
      this.unmountCanvas = mod?.mountCausalityCanvas?.(canvasHost, {
        eventTarget: document,
        onSelectEdge: (edge) => {
          this._selectedEdge = edge;
          this.renderInspector(edge);
        },
        onSelectNode: (node) => {
          this._selectedNode = node;
          this.renderSuggestions();
          this.renderNodeDetails(node);
          this.renderFiltersPanel(this._graph);
        },
        activeLenses: this._activeLenses, // Pass active lenses to canvas
        initialTransform,
        initialFilters,
        lensParams: { laneTitles: this._laneTitles || {} }
      }) || null;
    } catch (e) {
      log.error('Failed to mount canvas', e);
    }

    // Subscribe to canvas refresh to keep Focus panel updated even if timing differs
    try {
      const { default: Bus } = await import('../../services/graph/CanvasBus.js');
      this._offRefresh = Bus.on('causality:refresh-canvas', () => {
        try { this.renderStartNodePanel(this._graph); } catch(_) {}
      });
      this._offSetLens = Bus.on('causality:set-lens', () => {
        try { this.renderStartNodePanel(this._graph); } catch(_) {}
      });
    } catch(_) {}
  }

  toggleLens(lensType) {
    if (this._activeLenses.has(lensType)) {
      this._activeLenses.delete(lensType);
    } else {
      this._activeLenses.add(lensType);
    }
    
    // Update lens button states
    this.container.querySelectorAll('.lens-btn').forEach(btn => {
      const lens = btn.dataset.lens;
      if (this._activeLenses.has(lens)) {
        btn.classList.add('active');
      } else {
        btn.classList.remove('active');
      }
    });
    
    // Notify canvas to update lens filters
    const event = new CustomEvent('causality:lens-changed', {
      detail: { activeLenses: Array.from(this._activeLenses) }
    });
    document.dispatchEvent(event);
    
    log.info('Lens toggled', { lensType, active: this._activeLenses.has(lensType) });
  }

  async openOverlay(overlayType, data = {}) {
    // Overlays for impact-map and evidence-ledger are removed in simplified UI
    if (overlayType === 'impact-map' || overlayType === 'evidence-ledger') return;
    const container = this.container.querySelector('#overlay-container');
    if (!container) return;
    
    // Close existing overlay of same type
    if (this._overlayPanels.has(overlayType)) {
      this.closeOverlay(overlayType);
    }
    
    let content = '';
    let component = null;
    
    switch (overlayType) {
      case 'impact-map':
        // Impact Map drawer - special handling
        const drawerHost = this.container.querySelector('#impact-map-drawer-host');
        if (drawerHost) {
          const { ImpactMapDrawer } = await import('../../components/causality/ImpactMapDrawer.jsx');
          const { render } = await import('preact');
          const { h } = await import('preact');
          
          render(
            h(ImpactMapDrawer, {
              isOpen: true,
              onClose: () => {
                drawerHost.innerHTML = '';
                this._overlayPanels.delete('impact-map');
              },
              onComplete: () => {
                this.refreshCanvas();
              },
              liveCanvas: true // New prop - create nodes directly on canvas
            }),
            drawerHost
          );
          this._overlayPanels.set('impact-map', { type: 'drawer', host: drawerHost });
        }
        return;
        
      case 'lever-explorer':
        content = await this.renderLeverExplorerOverlay();
        break;
        
      case 'evidence-ledger':
        if (Config?.FEATURES?.CAUSALITY_READ_ONLY === true) return; // disable in read-only
        if (this._selectedEdge) {
          const ledgerHost = this.container.querySelector('#evidence-ledger-host');
          if (ledgerHost) {
            const { EvidenceLedger } = await import('../../components/causality/EvidenceLedger.jsx');
            const { render } = await import('preact');
            const { h } = await import('preact');
            
            render(
              h(EvidenceLedger, {
                edge: this._selectedEdge,
                onClose: () => {
                  ledgerHost.innerHTML = '';
                  this._overlayPanels.delete('evidence-ledger');
                },
                onUpdate: () => {
                  this.refreshCanvas();
                }
              }),
              ledgerHost
            );
            this._overlayPanels.set('evidence-ledger', { type: 'drawer', host: ledgerHost });
          }
        }
        return;
        
      case 'loop-narrator':
        content = await this.renderLoopNarrator(data.loopId);
        break;
    }
    
    if (content) {
      const overlayDiv = document.createElement('div');
      overlayDiv.className = `overlay-panel ${overlayType}`;
      overlayDiv.dataset.overlayId = overlayType;
      overlayDiv.innerHTML = content;
      container.appendChild(overlayDiv);
      this._overlayPanels.set(overlayType, { type: 'overlay', element: overlayDiv });
    }
  }

  closeOverlay(overlayType) {
    const overlay = this._overlayPanels.get(overlayType);
    if (!overlay) return;
    
    if (overlay.type === 'overlay' && overlay.element) {
      overlay.element.remove();
    } else if (overlay.type === 'drawer' && overlay.host) {
      overlay.host.innerHTML = '';
    }
    
    this._overlayPanels.delete(overlayType);
  }

  // Minimal Lever Explorer overlay to avoid runtime errors
  async renderLeverExplorerOverlay() {
    const html = `
      <div class="overlay-header">
        <h3>Lever Explorer</h3>
        <button class="btn btn-small btn-ghost" data-action="causality:close-overlay" data-overlay-id="lever-explorer">Close</button>
      </div>
      <div class="overlay-content">
        <p class="text-muted">Lever Explorer is coming soon. Use the Focus/Filters on the right to analyze levers meanwhile.</p>
      </div>
    `;
    return html;
  }

  async refreshCanvas() {
    if (!this._mounted || !this.container) return;
    
    const svc = container.get(T.CAUSALITY);
    await svc?.initialize?.();
    const graph = await this.loadFullGraph();
    this._graph = graph;

    // Build lane titles: graph-first (derive initiatives per objective), then Dexie fallback
    try {
      const laneTitles = this._computeLaneTitlesGraphFirst(graph);
      // Dexie fallback to catch any missing objectives
      const missing = new Set((graph.nodes||[]).filter(n=>n.kind==='objective').map(n=>String(n.id)));
      Object.keys(laneTitles).forEach(k=>missing.delete(String(k)));
      if (missing.size > 0) {
        const dexieMap = await this._computeLaneTitlesDexieFallback(graph);
        missing.forEach(oid => { if (!laneTitles[oid] && dexieMap[oid]) laneTitles[oid] = dexieMap[oid]; });
      }
      this._laneTitles = laneTitles;
    } catch (e) { this._laneTitles = {}; }
    
    // Update summary
    this.renderSummary(graph);
    
    // Render side panels
    await this.renderSidePanels(graph);
    await this.renderStartNodePanel(graph);
    await this.renderFiltersPanel(graph);
    await this.renderCanvasControlsPanel();
    if (this._selectedNode) await this.renderNodeDetails(this._selectedNode);
    
    // Update inspector if edge selected
    if (this._selectedEdge) {
      this.renderInspector(this._selectedEdge);
    }
    
    // Notify canvas to reload with all node types
    // Emit via CanvasBus (authoritative), keep DOM event for compatibility
    try {
      const { default: Bus } = await import('../../services/graph/CanvasBus.js');
      Bus.emit('causality:refresh-canvas', { 
        graph,
        activeLenses: Array.from(this._activeLenses),
        laneTitles: this._laneTitles || {}
      });
    } catch(_) {}
    try {
      const event = new CustomEvent('causality:refresh-canvas', {
        detail: { 
          graph,
          activeLenses: Array.from(this._activeLenses),
          laneTitles: this._laneTitles || {}
        }
      });
      document.dispatchEvent(event);
    } catch(_) {}
  }

  /** Derive lane titles using the current graph (initiatives influencing an objective's KRs, etc.) */
  _computeLaneTitlesGraphFirst(graph) {
    const nodes = graph?.nodes || [];
    const edges = graph?.edges || [];
    const byId = new Map(nodes.map(n => [String(n.id), n]));
    const byFrom = new Map();
    const byTo = new Map();
    for (const e of edges) {
      const s = String(e.fromId ?? e.fromExternalId ?? e.from ?? '');
      const t = String(e.toId ?? e.toExternalId ?? e.to ?? '');
      if (!byFrom.has(s)) byFrom.set(s, []); byFrom.get(s).push(e);
      if (!byTo.has(t)) byTo.set(t, []); byTo.get(t).push(e);
    }
    const laneTitles = {};
    const objectives = nodes.filter(n => n.kind === 'objective');
    for (const o of objectives) {
      const oid = String(o.id);
      // KRs driven by this objective
      const krs = (byFrom.get(oid) || [])
        .filter(e => String(e.type)==='drives')
        .map(e => byId.get(String(e.toId ?? e.toExternalId ?? e.to ?? '')))
        .filter(n => n && n.kind === 'key_result');
      // Score initiatives connected to these KRs via influences
      const score = new Map();
      for (const kr of krs) {
        const incoming = byTo.get(String(kr.id)) || [];
        for (const e of incoming) {
          const n = byId.get(String(e.fromId ?? e.fromExternalId ?? e.from ?? ''));
          if (n?.kind === 'initiative' && (String(e.type)==='influences' || String(e.type)==='drives')) {
            const k = String(n.id); score.set(k, (score.get(k)||0) + 3);
          }
        }
      }
      // Bonus: if objective influences stakeholders → enacted_by behaviors → requires deliverables ← produces initiatives
      const stakeholders = (byFrom.get(oid) || [])
        .filter(e => String(e.type)==='influences')
        .map(e => byId.get(String(e.toId ?? e.toExternalId ?? e.to ?? '')))
        .filter(n => n && n.kind === 'stakeholder');
      const behaviors = [];
      for (const s of stakeholders) {
        const incoming = byTo.get(String(s.id)) || [];
        incoming.filter(e => String(e.type)==='enacted_by')
          .forEach(e => { const b = byId.get(String(e.fromId ?? e.fromExternalId ?? e.from ?? '')); if (b?.kind==='behavior') behaviors.push(b); });
      }
      const deliverables = [];
      for (const b of behaviors) {
        const outgoing = byFrom.get(String(b.id)) || [];
        outgoing.filter(e => String(e.type)==='requires')
          .forEach(e => { const d = byId.get(String(e.toId ?? e.toExternalId ?? e.to ?? '')); if (d?.kind==='deliverable') deliverables.push(d); });
      }
      for (const d of deliverables) {
        const incoming = byTo.get(String(d.id)) || [];
        incoming.filter(e => String(e.type)==='produces')
          .forEach(e => { const n = byId.get(String(e.fromId ?? e.fromExternalId ?? e.from ?? '')); if (n?.kind==='initiative') { const k = String(n.id); score.set(k, (score.get(k)||0) + 1); } });
      }
      // Pick highest scoring initiative, fallback to objective Title/title
      let bestId = null, bestScore = -1;
      score.forEach((v, k) => { if (v > bestScore) { bestScore = v; bestId = k; } });
      if (bestId) {
        const n = byId.get(bestId);
        const t = String(n?.raw?.Title || n?.title || n?.raw?.title || '');
        laneTitles[oid] = t;
      } else {
        // leave empty; Dexie fallback will try; renderer will finally fallback to objective
        laneTitles[oid] = '';
      }
    }
    return laneTitles;
  }

  async _computeLaneTitlesDexieFallback(graph) {
    try {
      const { default: db } = await import('../../services/db/DexieDB.js');
      const [inits, links] = await Promise.all([
        db.table('initiatives').toArray(),
        db.table('initiative_links').toArray().catch(()=>[])
      ]);
      const byId = new Map(inits.map(i => [String(i.id), i]));
      const byObj = new Map();
      (links||[]).forEach(l => {
        const oid = String(l.objectiveId||''); const iid = String(l.initiativeId||'');
        if (!oid || !iid) return; const arr = byObj.get(oid)||[]; const ii = byId.get(iid); if (ii) arr.push(ii); byObj.set(oid, arr);
      });
      // Fallback: initiatives with objective/Object ive field matching objective title
      const objectives = (graph.nodes||[]).filter(n=>n.kind==='objective');
      const byTitle = new Map(objectives.map(o => [String((o.title||'').trim().toLowerCase()), String(o.id)]));
      inits.forEach(i => {
        const t = String((i.objective || i.Objective || '')).trim().toLowerCase();
        const oid = byTitle.get(t); if (!oid) return;
        const arr = byObj.get(oid)||[]; arr.push(i); byObj.set(oid, arr);
      });
      const laneTitles = {};
      objectives.forEach(o => {
        const oid = String(o.id);
        const list = (byObj.get(oid) || [])
          .slice()
          .sort((a, b) => String(a?.title || a?.Title || '').localeCompare(String(b?.title || b?.Title || '')));
        const chosen = (list[0]?.title || list[0]?.Title || '');
        laneTitles[oid] = String(chosen);
      });
      return laneTitles;
    } catch {
      return {};
    }
  }

  async loadFullGraph() {
    const svc = container.get(T.CAUSALITY);
    const graph = await svc.getGraph();
    
    // Enhance with cross-link analysis
    try {
      const CrossLink = await import('../../services/graph/CrossLinkProposals.js');
      const analysis = CrossLink.analyzeCrossLinks(graph);
      this._crossLinkProposals = analysis.proposals || [];
      this._crossLinkWarnings = analysis.warnings || [];
    } catch (e) {
      log.warn('Failed to analyze cross-links', e);
    }
    
    // Detect loops
    try {
      const analysis = await svc.analyzeLoops(graph);
      this._loops = analysis?.loops || [];
    } catch (e) {
      log.warn('Failed to analyze loops', e);
    }
    
    // Detect smells
    try {
      this._smells = await svc.detectSmells(graph);
    } catch (e) {
      log.warn('Failed to detect smells', e);
    }
    
    // Build filter options (owners)
    try {
      const owners = new Set();
      (graph.nodes || []).forEach(n => { const o = n?.raw?.owner; if (o) owners.add(String(o)); });
      this._ownerOptions = Array.from(owners.values()).sort();
    } catch(_) { this._ownerOptions = []; }
    
    return graph;
  }

  renderSummary(graph) {
    const summary = this.container.querySelector('#causality-summary');
    if (!summary) return;
    
    // Count node types
    const nodeCounts = {};
    graph.nodes.forEach(node => {
      nodeCounts[node.kind] = (nodeCounts[node.kind] || 0) + 1;
    });
    
    // Get KR reuse stats
    let multiParentKRs = 0;
    try {
      const CrossLink = { getKRReuseStats: () => new Map() }; // Stub if not available
      const reuseStats = CrossLink.getKRReuseStats(graph);
      multiParentKRs = Array.from(reuseStats.entries()).filter(([_, count]) => count > 1).length;
    } catch {}
    
    const nodeTypeLabels = {
      objective: 'Objectives',
      key_result: 'KRs',
      initiative: 'Initiatives',
      stakeholder: 'Stakeholders',
      behavior: 'Behaviors',
      deliverable: 'Deliverables'
    };
    
    const nodeTypesSummary = Object.entries(nodeCounts)
      .map(([type, count]) => `${nodeTypeLabels[type] || type}: ${count}`)
      .join(' • ');
    
    summary.innerHTML = `
      <div class="summary-stats">
        <span class="stat">Total Nodes: ${graph.nodes.length}</span>
        <span class="stat">Edges: ${graph.edges.length}</span>
        <span class="stat">${nodeTypesSummary}</span>
        ${multiParentKRs > 0 ? `<span class="stat highlight">Multi-parent KRs: ${multiParentKRs}</span>` : ''}
        ${this._loops?.length > 0 ? `<span class="stat">Loops: ${this._loops.length}</span>` : ''}
      </div>
    `;
  }

  async renderSidePanels(graph) {
    // Render loops panel
    const loopsPanel = this.container.querySelector('#loops-panel');
    if (loopsPanel && this._loops?.length > 0) {
      const loopsList = this._loops.map((loop, idx) => `
        <li class="loop-item" data-loop-idx="${idx}">
          <span class="loop-type ${loop.type}">${loop.type}</span>
          <span class="loop-path">${loop.nodes.slice(0, 3).join(' → ')}${loop.nodes.length > 3 ? '...' : ''}</span>
        </li>
      `).join('');
      
      loopsPanel.innerHTML = `
        <h4>Loops Detected</h4>
        <ul class="loops-list">${loopsList}</ul>
      `;
      
      // Add hover handlers
      loopsPanel.querySelectorAll('.loop-item').forEach(item => {
        item.addEventListener('mouseenter', () => {
          const idx = parseInt(item.dataset.loopIdx, 10);
          this.highlightLoop(idx);
        });
        item.addEventListener('mouseleave', () => {
          this.highlightLoop(null);
        });
      });
    } else if (loopsPanel) {
      loopsPanel.innerHTML = '';
    }
    
    // Render smells panel
    const smellsPanel = this.container.querySelector('#smells-panel');
    if (smellsPanel && this._smells?.length > 0) {
      const grouped = { error: [], warning: [], info: [] };
      this._smells.forEach(s => {
        const severity = s.severity || 'info';
        grouped[severity].push(s);
      });
      
      let smellsHtml = '<h4>Quality Checks</h4>';
      ['error', 'warning', 'info'].forEach(severity => {
        if (grouped[severity].length > 0) {
          smellsHtml += `
            <div class="smell-group ${severity}">
              <h5>${severity.toUpperCase()}</h5>
              <ul>
                ${grouped[severity].map(s => `<li>${s.message}</li>`).join('')}
              </ul>
            </div>
          `;
        }
      });
      
      smellsPanel.innerHTML = smellsHtml;
    } else if (smellsPanel) {
      smellsPanel.innerHTML = '';
    }
    
    // Render cross-link proposals
    const crossLinksPanel = this.container.querySelector('#cross-links-panel');
    if (crossLinksPanel && this._crossLinkProposals?.length > 0) {
      const topProposals = this._crossLinkProposals.slice(0, 3);
      const proposalsHtml = topProposals.map(p => `
        <li class="proposal-item">
          <div class="proposal-reason">${p.reason}</div>
          <div class="proposal-action">
            <span class="proposal-edge">${p.from} → ${p.to}</span>
            <button class="btn btn-xsmall btn-primary" 
                    data-action="causality:apply-cross-link" 
                    data-from="${p.from}" 
                    data-to="${p.to}" 
                    data-type="${p.type || 'influences'}">
              Apply
            </button>
          </div>
        </li>
      `).join('');
      
      crossLinksPanel.innerHTML = `
        <h4>🔗 Anti-Silo Proposals</h4>
        <ul class="proposals-list">${proposalsHtml}</ul>
      `;
    } else if (crossLinksPanel) {
      crossLinksPanel.innerHTML = '';
    }
    
    // Render suggestions if node selected
    if (this._selectedNode) {
      await this.renderSuggestions();
    }
  }

  async renderStartNodePanel(graph) {
    const host = this.container.querySelector('#start-node-panel');
    if (!host) return;
    try {
      const { h } = await import('preact');
      const { render } = await import('preact');
      const { StartNodePicker } = await import('../../components/causality/StartNodePicker.jsx');
      const defaultId = this._focusedNodeId || '';
      const onFocus = async (id) => {
        this._focusedNodeId = id || null;
        try { const mod = await import('../../services/graph/CanvasBus.js'); mod.default.focusNode(id); } catch(_) {}
        try { this.renderFiltersPanel(this._graph); } catch(_) {}
      };
      // Build lane-title options for Impact lens; ensure complete list without full refresh
      let options = null; let pickerTitle = 'Focus Node';
      try {
        const lensRaw = (localStorage.getItem('causality.lens')||'impact');
        const lens = String(lensRaw).toLowerCase();
        const isImpact = lens.includes('impact');
        if (isImpact && graph) {
          pickerTitle = 'Focus on Title';
          const titles = [];
          // Prefer authoritative list from canvas (includes derived objectives)
          const canvasMap = this._laneTitlesFromCanvas || null;
          if (canvasMap && Object.keys(canvasMap).length) {
            Object.entries(canvasMap).forEach(([id, label]) => {
              titles.push({ id: String(id), label: String(label || '') });
            });
          }
          // Also include all initiative Titles mapped to their objective lanes
          try {
            const nodes = graph.nodes || []; const edges = graph.edges || [];
            const byId = new Map(nodes.map(n => [String(n.id), n]));
            const byFrom = new Map(); const byTo = new Map();
            edges.forEach(e => { const s=String(e.fromId??e.fromExternalId??e.from??''); const t=String(e.toId??e.toExternalId??e.to??''); if (!byFrom.has(s)) byFrom.set(s, []); byFrom.get(s).push(e); if (!byTo.has(t)) byTo.set(t, []); byTo.get(t).push(e); });
            const objByTitle = new Map(nodes.filter(n=>n.kind==='objective').map(o=>[String((o.title||'').trim().toLowerCase()), String(o.id)]));
            // KR -> Objective map via 'drives'
            const krToObj = new Map();
            edges.filter(e => String(e.type)==='drives').forEach(e => { const from=String(e.fromId??e.fromExternalId??e.from??''); const to=String(e.toId??e.toExternalId??e.to??''); const fromNode=byId.get(from); const toNode=byId.get(to); if (fromNode?.kind==='objective' && toNode?.kind==='key_result') krToObj.set(to, from); });
            nodes.filter(n => n.kind==='initiative').forEach(init => {
              const label = String(init?.raw?.Title || init?.title || '').trim(); if (!label) return;
              let oid = null;
              // 1) From KR links
              const outs = byFrom.get(String(init.id)) || [];
              for (const e of outs) { const tgt = byId.get(String(e.toId??e.toExternalId??e.to??'')); if (tgt?.kind==='key_result') { const o = krToObj.get(String(tgt.id)); if (o) { oid = String(o); break; } } }
              // 2) From initiative.raw.objective match
              if (!oid) {
                const t = String(init?.raw?.objective || '').trim().toLowerCase(); if (t) oid = objByTitle.get(t) || null;
              }
              if (oid) {
                const id = `lane:${oid}`;
                // Do not de-duplicate to preserve all Titles
                titles.push({ id, label });
              }
            });
          } catch(_) {}
          // If still empty, fallback: build from full graph objectives and computed laneTitles
          if (titles.length === 0) {
            const objectives = (graph.nodes||[]).filter(n => n.kind==='objective');
            const laneTitles = this._laneTitles || {};
            objectives.forEach(o => {
              const oid = String(o.id);
              titles.push({ id: `lane:${oid}`, label: String(laneTitles[oid] || o.title || oid) });
            });
          }
          // 2) If canvas has already rendered lane_title nodes, prefer their visible text labels
          try {
            const host = this.container?.querySelector('#causality-canvas-host');
            const nodes = Array.from(host?.querySelectorAll('g.node.lane_title') || []);
            const byId = new Map();
            for (const n of nodes) {
              const id = n.getAttribute('data-node-id') || '';
              const txt = (n.querySelector('.node-title')?.textContent || '').trim();
              if (id && txt) byId.set(id, txt);
            }
            if (byId.size > 0) {
              titles.forEach(t => { const v = byId.get(t.id); if (v) t.label = v; });
            }
          } catch (_) {}
          // 3) Sort for stable UX
          titles.sort((a,b) => String(a.label||'').localeCompare(String(b.label||'')));
          options = titles;
        }
      } catch(_) {}
      const extra = document.createElement('div');
      extra.style.display = 'flex';
      extra.style.gap = '8px';
      extra.style.marginTop = '8px';
      const lensNow = (localStorage.getItem('causality.lens')||'impact');
      const labelImpact = lensNow === 'okr' ? 'Key Result' : 'Impact';
      extra.innerHTML = `
        <button class="btn btn-small" id="add-stakeholder">+ Add Stakeholder</button>
        <button class="btn btn-small" id="add-impact">+ Add ${labelImpact}</button>
        <button class="btn btn-small" id="add-objective">+ Add Objective</button>
        <button class="btn btn-small" id="add-deliverable">+ Add Deliverable</button>
      `;
      host.innerHTML = '';
      render(h(StartNodePicker, { onFocus, defaultId, options, title: pickerTitle }), host);
      // No quick-add buttons here; use node-based actions only
    } catch (e) {
      host.innerHTML = `<div class="panel-lite"><h4>Focus Node</h4><p class="text-muted">Failed to load picker</p></div>`;
    }
  }

  async renderNodeDetails(node) {
    const host = this.container.querySelector('#details-panel');
    if (!host) return;
    if (!node) { host.innerHTML = ''; return; }
    try {
      const { h } = await import('preact');
      const { render } = await import('preact');
      const { NodeDetails } = await import('../../components/causality/NodeDetails.jsx');
      render(h(NodeDetails, { node, onClose: () => { this._selectedNode = null; this.renderNodeDetails(null); } }), host);
    } catch (e) {
      host.innerHTML = `<div class="panel-lite"><h4>Details</h4><p>${node.title || node.id}</p></div>`;
    }
  }

  async renderFiltersPanel(graph) {
    const panel = this.container.querySelector('#filters-panel');
    if (!panel) return;
    const owners = this._ownerOptions || [];
    // Load persisted filters (owners, onlyLinked, ownerLinkedMode, stacked)
    let persisted = null;
    try { persisted = JSON.parse(localStorage.getItem('causality.filters') || 'null'); } catch(_) { persisted = null; }
    const initFilters = persisted && typeof persisted === 'object' ? persisted : (this._filters || { owners: [], onlyLinked: false, ownerLinkedMode: 'none' });
    this._filters = initFilters;
    const selectedNode = this._selectedNode;
    const readOnly = (Config?.FEATURES?.CAUSALITY_READ_ONLY === true);
    const ownerOptions = owners.map(o => `<option value="${o}">${o}</option>`).join('');
    panel.innerHTML = `
      <div class="panel-lite">
        <h4 style="margin:0 0 8px">Quick Filters</h4>
        ${this._focusedNodeId ? `<div class="badge" style="display:inline-block;margin-bottom:8px;padding:2px 8px;border-radius:999px;background:var(--color-primary-bg);color:var(--color-primary);font-size:12px;">Focused: ${this._focusedNodeId}</div>` : ''}
        <div class="form-group">
          <label>Owner</label>
          <select id="filter-owners" class="form-control" multiple size="4">${ownerOptions}</select>
          <small class="text-muted">Hold Cmd/Ctrl to multi-select</small>
        </div>
        <div class="form-group" id="owner-scope-group">
          <div style="margin:4px 0;color:var(--color-text-secondary);">Linked hops around Owners</div>
          <label style="margin-right:8px;"><input type="radio" name="owner-linked-scope" value="none"/> None</label>
          <label style="margin-right:8px;"><input type="radio" name="owner-linked-scope" value="1hop"/> One hop</label>
          <label><input type="radio" name="owner-linked-scope" value="network"/> Full network</label>
        </div>
        <div class="form-group">
          <label><input type="checkbox" id="filter-only-linked"/> Hide isolated nodes</label>
        </div>
        ${(() => { try { return (localStorage.getItem('causality.lens')||'impact')==='okr'; } catch(_) { return false; } })() ? `
        <div class="form-group">
          <label><input type="checkbox" id="filter-stacked"/> Stack view</label>
          <small class="text-muted">OKR cards: Title → Objective → KRs</small>
        </div>` : ''}
        <div class="btn-row" style="display:flex;gap:8px;">
          <button class="btn btn-small btn-primary" id="apply-filters">Apply</button>
          <button class="btn btn-small btn-ghost" id="clear-filters">Clear</button>
        </div>
      </div>
    `;
    const byId = (id) => panel.querySelector(id);
    // Prefill controls from persisted filters
    try {
      const ownersSel = byId('#filter-owners');
      if (ownersSel && Array.isArray(initFilters.owners)) {
        const values = new Set((initFilters.owners||[]).map(String));
        Array.from(ownersSel.options).forEach(opt => { opt.selected = values.has(opt.value); });
      }
      const scope = initFilters.ownerLinkedMode || 'none';
      const radio = panel.querySelector(`input[name="owner-linked-scope"][value="${scope}"]`);
      if (radio) radio.checked = true;
      byId('#filter-only-linked').checked = !!initFilters.onlyLinked;
      const lensNow = (localStorage.getItem('causality.lens')||'impact');
      if (lensNow==='okr' && byId('#filter-stacked')) byId('#filter-stacked').checked = !!(localStorage.getItem('causality.stacked')==='true');
    } catch(_) {}
    byId('#apply-filters').addEventListener('click', async () => {
      const sel = Array.from(byId('#filter-owners').selectedOptions || []).map(o => o.value);
      const onlyLinked = byId('#filter-only-linked').checked;
      const ownerLinkedMode = (panel.querySelector('input[name="owner-linked-scope"]:checked')?.value || 'none');
      const stacked = !!byId('#filter-stacked')?.checked;
      this._filters = { owners: sel, onlyLinked, ownerLinkedMode };
      try { localStorage.setItem('causality.filters', JSON.stringify(this._filters)); } catch(_) {}
      try {
        const { default: Bus } = await import('../../services/graph/CanvasBus.js');
        Bus.applyFilters({ owners: sel, onlyLinked, ownerLinkedMode });
        // Stacked applies only for OKR lens
        const lens = (localStorage.getItem('causality.lens')||'impact');
        Bus.emit('causality:set-stacked', { stacked: lens==='okr' ? stacked : false });
      } catch(_) {
        try { document.dispatchEvent(new CustomEvent('causality:apply-filters', { detail: { owners: sel, onlyLinked, ownerLinkedMode } })); } catch(_) {}
        try { const lens=(localStorage.getItem('causality.lens')||'impact'); document.dispatchEvent(new CustomEvent('causality:set-stacked', { detail: { stacked: lens==='okr' ? stacked : false } })); } catch(_) {}
      }
    });
    byId('#clear-filters').addEventListener('click', async () => {
      try {
        const { default: Bus } = await import('../../services/graph/CanvasBus.js');
        Bus.applyFilters({ owners: [], onlyLinked: false, ownerLinkedMode: 'none' });
      } catch(_) {
        try { document.dispatchEvent(new CustomEvent('causality:apply-filters', { detail: { owners: [], onlyLinked: false, ownerLinkedMode: 'none' } })); } catch(_) {}
      }
      this.renderFiltersPanel(this._graph);
    });
    // Do not auto-apply here; render() orchestrates single initial apply to avoid double-renders.
  }

  async renderCanvasControlsPanel() {
    const panel = this.container.querySelector('#canvas-controls-panel');
    if (!panel) return;
    const readOnly = (Config?.FEATURES?.CAUSALITY_READ_ONLY === true);
    panel.innerHTML = `
      <div class="panel-lite">
        <h4 style="margin:0 0 8px">Canvas Controls</h4>
        <div class="form-group">
          <div style="margin:4px 0;color:var(--color-text-secondary);">Node types</div>
          <label style="margin-right:12px;"><input id="ctl-show-components" type="checkbox"> Components</label>
          <label style="margin-right:12px;"><input id="ctl-show-levers" type="checkbox"> Levers</label>
          <label><input id="ctl-show-risks" type="checkbox" checked> Risks</label>
        </div>
        <div class="form-group">
          <label>Strength ≥ <span id="ctl-strength-val">0.0</span></label>
          <input id="ctl-strength" type="range" min="0" max="1" step="0.1" value="0" class="form-control" />
        </div>
        <div class="form-group">
          <label>Confidence ≥ <span id="ctl-confidence-val">0.0</span></label>
          <input id="ctl-confidence" type="range" min="0" max="1" step="0.1" value="0" class="form-control" />
        </div>
        <div class="form-group">
          <label>Max hops from focus <span id="ctl-hops-val">∞</span></label>
          <input id="ctl-hops" type="range" min="1" max="6" step="1" value="3" class="form-control" />
          <div class="btn-row" style="display:flex;gap:8px;margin-top:6px;">
            <button id="ctl-clear-focus" class="btn btn-small">Clear focus</button>
          </div>
        </div>
        <div class="form-group">
          <div style="margin:4px 0;color:var(--color-text-secondary);">Overlays</div>
          <label style="margin-right:12px;"><input id="ctl-lvt-rails" type="checkbox"> LVT rails (Z lanes)</label>
          <div style="margin-top:6px;">Wardley Z: <span id="ctl-wardley-val">0</span></div>
          <input id="ctl-wardley" type="range" min="0" max="200" step="10" value="0" class="form-control" />
        </div>
        ${readOnly ? '' : `<div class="form-group"><label><input id="ctl-link-mode" type="checkbox"> Interactive link mode</label></div>`}
      </div>
    `;
    const $ = (sel) => panel.querySelector(sel);
    const send = () => {
      import('../../services/graph/CanvasBus.js').then(mod => {
        const Bus = mod.default;
        const hops = parseInt($('#ctl-hops').value, 10);
        Bus.setControls({
          showComponents: $('#ctl-show-components').checked,
          showLevers: $('#ctl-show-levers').checked,
          showRisks: $('#ctl-show-risks').checked,
          strengthMin: parseFloat($('#ctl-strength').value),
          confidenceMin: parseFloat($('#ctl-confidence').value),
          maxHops: Number.isFinite(hops) ? hops : Infinity,
          lvtRails: $('#ctl-lvt-rails').checked,
          wardleyZ: parseInt($('#ctl-wardley').value, 10),
          linkMode: $('#ctl-link-mode') ? $('#ctl-link-mode').checked : undefined
        });
      }).catch(()=>{});
    };
    const bind = (id, fn) => { const el = $(id); if (el) el.addEventListener('input', fn); if (el) el.addEventListener('change', fn); };
    bind('#ctl-show-components', send);
    bind('#ctl-show-levers', send);
    bind('#ctl-show-risks', send);
    bind('#ctl-strength', () => { $('#ctl-strength-val').textContent = parseFloat($('#ctl-strength').value).toFixed(1); send(); });
    bind('#ctl-confidence', () => { $('#ctl-confidence-val').textContent = parseFloat($('#ctl-confidence').value).toFixed(1); send(); });
    bind('#ctl-hops', () => { const v=parseInt($('#ctl-hops').value,10); $('#ctl-hops-val').textContent = String(v); send(); });
    bind('#ctl-lvt-rails', send);
    bind('#ctl-wardley', () => { $('#ctl-wardley-val').textContent = String(parseInt($('#ctl-wardley').value,10)); send(); });
    const clearBtn = $('#ctl-clear-focus'); if (clearBtn) clearBtn.addEventListener('click', () => { import('../../services/graph/CanvasBus.js').then(m=>m.default.setControls({ clearFocus: true, maxHops: 'infinite' })); });
    if ($('#ctl-link-mode')) $('#ctl-link-mode').addEventListener('change', send);
    // Initial emit to sync defaults
    send();
  }

  async renderSuggestions() {
    const panel = this.container?.querySelector('#suggestions-panel');
    if (!panel) return;
    
    const node = this._selectedNode;
    if (!node) {
      panel.innerHTML = '';
      return;
    }
    
    const svc = container.get(T.CAUSALITY);
    const candidates = await svc.suggestLinksLocal(node, { direction: 'out', limit: 5 });
    
    const list = candidates.map(c => `
      <li class="suggestion-item">
        <span class="suggestion-target">${c.title || c.id}</span>
        <span class="suggestion-score">${(c.score * 100).toFixed(0)}%</span>
        <button class="btn btn-xsmall" 
                data-action="causality:create-edge" 
                data-from="${node.id}" 
                data-to="${c.id}">
          Add
        </button>
      </li>
    `).join('');
    
    panel.innerHTML = `
      <h4>Suggested Links</h4>
      <div class="selected-node">From: ${node.title || node.id}</div>
      <ul class="suggestions-list">${list || '<li class="empty">No suggestions</li>'}</ul>
    `;
  }

  async renderInspector(edge) {
    const inspector = this.container?.querySelector('#details-panel');
    if (!inspector) return;
    if (!edge) {
      inspector.innerHTML = '';
      inspector.classList.remove('active');
      return;
    }
    const { h, render } = await import('preact');
    const { EdgeInspector } = await import('../../components/causality/EdgeInspector.jsx');
    const e = edge;
    const readOnly = (Config?.FEATURES?.CAUSALITY_READ_ONLY === true);
    const onSave = async (patch) => {
      if (readOnly) return;
      try {
        const svcMod = await import('../../services/CausalGraphService.js');
        const svc = svcMod.default;
        const updated = await svc.updateEdge(parseInt(e.id, 10), patch);
        try { 
          const { default: Bus } = await import('../../services/graph/CanvasBus.js'); 
          // Refresh canvas to reflect updates
          Bus.emit('causality:refresh-canvas');
        } catch(_) {}
        console.log('Edge updated', updated);
      } catch (err) {
        console.error('Failed to save edge', err);
      }
    };
    const onDelete = async () => {
      if (readOnly) return;
      try {
        const svcMod = await import('../../services/CausalGraphService.js');
        const svc = svcMod.default;
        await svc.deleteEdge(parseInt(e.id, 10));
        try { 
          const { default: Bus } = await import('../../services/graph/CanvasBus.js'); 
          Bus.emit('causality:refresh-canvas');
        } catch(_) {}
        this._selectedEdge = null;
        inspector.innerHTML = '';
        inspector.classList.remove('active');
      } catch (err) {
        console.error('Failed to delete edge', err);
      }
    };
    // Render into the details panel, replacing any node details for clarity
    inspector.innerHTML = '';
    render(h(EdgeInspector, { edge: e, onSave, onDelete, readOnly }), inspector);
  }

  // Overlay helpers removed in simplified UI

  highlightLoop(loopIdx) {
    const ids = [];
    try {
      if (typeof loopIdx === 'number' && this._loops && this._loops[loopIdx] && this._graph) {
        const loop = this._loops[loopIdx];
        const nodes = loop.nodes || [];
        for (let i = 0; i < nodes.length; i++) {
          const a = nodes[i];
          const b = nodes[(i + 1) % nodes.length];
          const e = this._graph.edges?.find(x => x.fromExternalId === a && x.toExternalId === b);
          if (e?.id != null) ids.push(String(e.id));
        }
      }
    } catch (_) {}
    
    try { CanvasBus.highlightEdges(ids); } catch(_) {}
  }

  focusNode(nodeId) {
    try { CanvasBus.focusNode(nodeId); } catch(_) {}
  }

  unmount() {
    if (this.unmountCanvas) {
      this.unmountCanvas();
      this.unmountCanvas = null;
    }
    
    if (this._quickCreateHandler) {
      document.removeEventListener('causality:quick-create-edge', this._quickCreateHandler);
      this._quickCreateHandler = null;
    }
    
    if (this._offLaneTitles) { try { this._offLaneTitles(); } catch(_) {} this._offLaneTitles = null; }

    if (this._offRefresh) { try { this._offRefresh(); } catch(_) {} this._offRefresh = null; }
    if (this._offSetLens) { try { this._offSetLens(); } catch(_) {} this._offSetLens = null; }
    this._mounted = false;
    this._overlayPanels.clear();
    this._activeLenses.clear();
  }
}

export default CausalityModule;
